'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_THEME,
  THEME_CLASS_MAP,
  THEME_CONFIGS,
  THEME_NAMES,
  THEME_STORAGE_KEY,
  type ThemeName,
} from '@microservices/design-system';

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredTheme(): ThemeName | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored && THEME_NAMES.includes(stored as ThemeName)) {
    return stored as ThemeName;
  }
  return null;
}

/**
 * Resolves the effective theme to apply.
 * When themeName is 'system', returns the OS preference.
 */
function resolveEffectiveTheme(themeName: ThemeName): Exclude<ThemeName, 'system'> {
  if (themeName === 'system') {
    return getSystemTheme();
  }
  return themeName;
}

function applyThemeClasses(themeName: ThemeName): void {
  if (typeof document === 'undefined') return;

  const html = document.documentElement;

  // Remove all theme classes
  html.classList.remove('dark', 'high-contrast');

  // Resolve 'system' to actual theme based on OS preference
  const effectiveTheme = resolveEffectiveTheme(themeName);

  // Apply new theme classes
  const classes = THEME_CLASS_MAP[effectiveTheme];
  if (classes) {
    classes.split(' ').forEach((cls) => {
      if (cls) html.classList.add(cls);
    });
  }
}

/**
 * Clears stored theme preference and resets to system default.
 * Call this on logout to ensure next user gets OS preference.
 */
export function resetThemeToSystem(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(THEME_STORAGE_KEY);
  applyThemeClasses(DEFAULT_THEME);
}

export function useTheme() {
  const [themeName, setThemeNameState] = useState<ThemeName>(DEFAULT_THEME);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const storedTheme = getStoredTheme();
    const initialTheme = storedTheme ?? DEFAULT_THEME;
    setThemeNameState(initialTheme);
    applyThemeClasses(initialTheme);
    setIsInitialized(true);
  }, []);

  // Listen for OS theme changes when using 'system' theme
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      const storedTheme = getStoredTheme();
      // Reapply classes when OS preference changes and theme is 'system' (or no stored theme)
      if (!storedTheme || storedTheme === 'system') {
        applyThemeClasses('system');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setThemeName = useCallback((newTheme: ThemeName) => {
    setThemeNameState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    applyThemeClasses(newTheme);
  }, []);

  // Get the effective theme config (resolve 'system' to actual theme)
  const effectiveTheme = resolveEffectiveTheme(themeName);
  const themeConfig = THEME_CONFIGS[effectiveTheme];

  return {
    themeName,
    setThemeName,
    themeConfig,
    isInitialized,
    isDark: themeConfig.mode === 'dark',
    isHighContrast: themeConfig.isHighContrast,
  };
}
